import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const BASE = "fixed z-50 grid gap-4 border border-border bg-card shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out motion-reduce:animate-none focus:outline-none [&>*]:min-w-0"
const CENTER = "left-[50%] top-[50%] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] p-6 rounded-[20px] data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
// sheet: na mobilu okno vyjede zespodu a nahoře nechá volno (stavová lišta
// + 40 px), ať ✕ nikdy nezasahuje do baterky/hodin. Od md: klasické okno uprostřed.
const SHEET = "p-6 max-md:inset-x-0 max-md:bottom-0 max-md:w-full max-md:max-w-none max-md:rounded-t-[26px] max-md:rounded-b-none max-md:border-x-0 max-md:border-b-0 max-md:max-h-[calc(100dvh-env(safe-area-inset-top)-40px)] max-md:pt-7 max-md:pb-[max(1.5rem,env(safe-area-inset-bottom))] max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom md:left-[50%] md:top-[50%] md:w-full md:max-w-lg md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-[20px] md:data-[state=closed]:fade-out-0 md:data-[state=open]:fade-in-0 md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95"

const setRef = (r, v) => { if (typeof r === "function") r(v); else if (r) r.current = v }

const DialogContent = React.forwardRef(({ className, children, sheet = false, hideClose = false, ...props }, ref) => {
  const innerRef = React.useRef(null)
  const closeRef = React.useRef(null)
  const drag = React.useRef(null)
  // Stažení dolů za úchyt zavře okno (jen mobil / sheet).
  const onDown = (e) => {
    if (!sheet || !innerRef.current) return
    drag.current = { y: e.clientY, dy: 0 }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const onMove = (e) => {
    if (!drag.current || !innerRef.current) return
    const dy = Math.max(0, e.clientY - drag.current.y)
    drag.current.dy = dy
    innerRef.current.style.transition = "none"
    innerRef.current.style.transform = `translateY(${dy}px)`
  }
  const onUp = () => {
    if (!drag.current || !innerRef.current) return
    const { dy } = drag.current
    drag.current = null
    innerRef.current.style.transition = ""
    innerRef.current.style.transform = ""
    if (dy > 90) closeRef.current?.click()
  }
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={(v) => { innerRef.current = v; setRef(ref, v) }}
        className={cn(BASE, sheet ? SHEET : CENTER, className)}
        {...props}>
        {sheet && (
          <div aria-hidden="true" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
            className="md:hidden absolute top-0 inset-x-0 h-7 z-10 flex justify-center pt-2.5 touch-none cursor-grab" data-testid="sheet-grabber">
            <span className="w-10 h-[5px] rounded-full bg-muted-foreground/35" />
          </div>
        )}
        {children}
        <DialogPrimitive.Close ref={closeRef}
          className={cn("absolute right-4 top-4 rounded-full p-1 opacity-70 ring-offset-background transition-opacity hover:opacity-100 hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
            sheet && "max-md:top-6 max-md:right-4 max-md:h-9 max-md:w-9 max-md:grid max-md:place-items-center max-md:bg-secondary max-md:opacity-100",
            hideClose && "sr-only")}>
          <X className="h-4 w-4" />
          <span className="sr-only">Zavřít</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    // Místo pro křížek vpravo nahoře (175) — nadpis ani akce pod něj nezalezou.
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left px-9 sm:px-0 sm:pr-10", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
